import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Chip, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Alert, Tabs, Tab} from '@mui/material';
import Layout from '../components/Layout';
import ExpandableTable from '../components/ExpandableTable';
import api from '../services/api';
import FileUpload from '../components/FileUpload';
import ReceiptIcon from '@mui/icons-material/Receipt';

export default function SubcontractorInvoice() {
  const [contracts, setContracts] = useState([]);
  const [selectedContract, setSelectedContract] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [invoiceType, setInvoiceType] = useState('platform');
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [addressFilter, setAddressFilter] = useState('all');
  const [form, setForm] = useState({
    amount: '',
    notes: '',
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    attachments: [],
  });

  useEffect(() => {
    api.get('/contracts').then(res => {
      setContracts(Array.isArray(res.data) ? res.data.filter(c => c.status === 'active') : []);
    }).catch(() => setContracts([]));
  }, []);

  const handleSubmit = async () => {
    if (!form.amount) {
      setAlert({ show: true, message: 'Please enter an amount', severity: 'error' });
      return;
    }
    setLoading(true);
    try {
      await api.post('/invoices', {
        contract_id: selectedContract.id,
        amount: parseFloat(form.amount),
        notes: form.notes,
        invoice_number: form.invoice_number || undefined,
        invoice_date: form.invoice_date,
        attachments: (form.attachments || []).length > 0 ? (form.attachments || []).map(f => ({ url: f.url || f.gcsUrl, name: f.name || f.originalName, size: f.size })) : undefined,
        is_external: form.is_external,
        customer_name: form.customer_name || undefined,
        customer_email: form.customer_email || undefined,
        customer_address: form.customer_address || undefined,
      });
      setAlert({ show: true, message: 'Invoice submitted successfully!', severity: 'success' });
      setDialogOpen(false);
      setForm({ amount: '', notes: '', invoice_number: '', invoice_date: new Date().toISOString().split('T')[0], is_external: false, customer_name: '', customer_email: '', customer_address: '', attachments: [] });
      setInvoiceType('platform');
    } catch (err) {
      setAlert({ show: true, message: err.response?.data?.error || 'Failed to submit invoice', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>Submit Invoice</Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>Submit invoices for your active contracts</Typography>

        {alert.show && (
          <Alert severity={alert.severity} sx={{ mb: 2 }} onClose={() => setAlert({ ...alert, show: false })}>
            {alert.message}
          </Alert>
        )}

        {(() => {
          const outstandingContracts = contracts.filter(c => {
            const rem = (parseFloat(c.contract_amount)||0) - (parseFloat(c.invoiced_to_date)||0);
            return rem > 0;
          });
          const fullyInvoiced = contracts.filter(c => {
            const rem = (parseFloat(c.contract_amount)||0) - (parseFloat(c.invoiced_to_date)||0);
            return rem <= 0;
          });
          const allAddresses = [...new Set(contracts.map(c => c.project_address).filter(Boolean))];
          const visibleRaw = activeTab === 0 ? outstandingContracts : fullyInvoiced;
          const visible = addressFilter === 'all' ? visibleRaw : visibleRaw.filter(c => c.project_address === addressFilter);
          return (
            <>
              <Tabs value={activeTab} onChange={(e,v)=>setActiveTab(v)} sx={{ mb: 2, borderBottom: '1px solid #e0e0e0' }}>
                <Tab label={`Outstanding (${outstandingContracts.length})`} />
                <Tab label={`Fully Invoiced (${fullyInvoiced.length})`} />
              </Tabs>
              {allAddresses.length > 1 && (
                <Box sx={{ mb: 2 }}>
                  <select value={addressFilter} onChange={e => setAddressFilter(e.target.value)}
                    style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #c5cae9', fontSize: 14, minWidth: 260, background: 'white', cursor: 'pointer' }}>
                    <option value="all">All Addresses ({contracts.length})</option>
                    {allAddresses.map(a => <option key={a} value={a}>{a} ({contracts.filter(c => c.project_address === a).length})</option>)}
                  </select>
                </Box>
              )}
              {contracts.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
                  <Typography>No active contracts found. Contracts must be active before submitting invoices.</Typography>
                </Box>
              ) : visible.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
                  <Typography>{activeTab === 0 ? 'No outstanding contracts — all invoices are submitted.' : 'No fully invoiced contracts yet.'}</Typography>
                </Box>
              ) : (
                <ExpandableTable
                  rows={visible.slice().sort((a, b) => (a.vendor_name || [a.first_name, a.last_name].filter(Boolean).join(' ') || '').toLowerCase().localeCompare((b.vendor_name || [b.first_name, b.last_name].filter(Boolean).join(' ') || '').toLowerCase()))}
                  emptyMessage={activeTab === 0 ? 'No outstanding contracts.' : 'No fully invoiced contracts yet.'}
                  columns={[
                    {
                      key: 'vendor_name',
                      label: 'Vendor',
                      render: (c) => (
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a237e' }} noWrap>
                            {c.vendor_name || [c.first_name, c.last_name].filter(Boolean).join(' ') || '\u2014'}
                          </Typography>
                          {c.scope_title && <Typography variant="caption" sx={{ color: '#888' }} noWrap>{c.scope_title}</Typography>}
                        </Box>
                      )
                    },
                    {
                      key: 'contract_num',
                      label: 'Contract #',
                      width: 130,
                      render: (c) => (
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 12, color: '#1a237e' }}>
                          {c.contract_number || (c.id ? c.id.slice(-8).toUpperCase() : '\u2014')}
                        </Typography>
                      )
                    },
                    {
                      key: 'po_number',
                      label: 'PO #',
                      width: 130,
                      render: (c) => (
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#555', fontSize: 12 }}>
                          {c.po_number || '\u2014'}
                        </Typography>
                      )
                    },
                    {
                      key: 'contract_amount',
                      label: 'Contract',
                      align: 'right',
                      width: 110,
                      render: (c) => (
                        <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                          ${(parseFloat(c.contract_amount) || 0).toLocaleString()}
                        </Typography>
                      )
                    },
                    {
                      key: 'invoiced_to_date',
                      label: 'Invoiced',
                      align: 'right',
                      width: 110,
                      render: (c) => (
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#e65100', fontVariantNumeric: 'tabular-nums' }}>
                          ${(parseFloat(c.invoiced_to_date) || 0).toLocaleString()}
                        </Typography>
                      )
                    },
                    {
                      key: 'remaining',
                      label: 'Remaining',
                      align: 'right',
                      width: 120,
                      render: (c) => {
                        const rem = (parseFloat(c.contract_amount) || 0) - (parseFloat(c.invoiced_to_date) || 0);
                        return (
                          <Typography variant="body2" sx={{ fontWeight: 700, color: rem <= 0 ? '#c62828' : '#2e7d32', fontVariantNumeric: 'tabular-nums' }}>
                            ${rem.toLocaleString()}
                          </Typography>
                        );
                      }
                    }
                  ]}
                  renderExpanded={(contract) => {
                    const contractAmt = parseFloat(contract.contract_amount) || 0;
                    const poAmt = parseFloat(contract.po_amount) || 0;
                    const invoicedAmt = parseFloat(contract.invoiced_to_date) || 0;
                    const paidAmt = parseFloat(contract.paid_to_date) || 0;
                    const remaining = contractAmt - invoicedAmt;
                    const fmt = v => '$' + v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                    return (
                      <Box>
                        <Box sx={{ display: 'flex', mb: 2, background: '#f5f6fa', borderRadius: 2, overflow: 'hidden', border: '1px solid #e0e0e0', flexWrap: 'wrap' }}>
                          {[
                            { label: 'Contract Amount', value: fmt(contractAmt), color: '#1a237e' },
                            ...(poAmt > 0 ? [{ label: 'PO Amount', value: fmt(poAmt), color: '#1a237e' }] : []),
                            { label: 'Invoiced to Date', value: fmt(invoicedAmt), color: '#e65100' },
                            ...(paidAmt > 0 ? [{ label: 'Paid to Date', value: fmt(paidAmt), color: '#2e7d32' }] : []),
                            { label: 'Remaining', value: fmt(remaining), color: remaining <= 0 ? '#c62828' : '#2e7d32' },
                          ].map((m, i, arr) => (
                            <Box key={m.label} sx={{ flex: 1, minWidth: 130, textAlign: 'center', p: 1.5, borderRight: i < arr.length - 1 ? '1px solid #e0e0e0' : 'none' }}>
                              <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>{m.label}</Typography>
                              <Typography sx={{ fontWeight: 700, color: m.color, fontSize: 16 }}>{m.value}</Typography>
                            </Box>
                          ))}
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', borderTop: '1px solid #f0f0f0', pt: 1.5 }}>
                          <Button variant="contained" size="small" startIcon={<ReceiptIcon />} disabled={remaining <= 0}
                            onClick={() => {
                              setSelectedContract(contract);
                              setForm({
                                amount: remaining > 0 ? remaining.toFixed(2) : '',
                                notes: '',
                                invoice_number: '',
                                invoice_date: new Date().toISOString().split('T')[0],
                              });
                              setDialogOpen(true);
                            }}
                            sx={{ borderRadius: 2, fontSize: 12 }}>
                            {remaining <= 0 ? 'Fully Invoiced' : 'Submit Invoice'}
                          </Button>
                        </Box>
                      </Box>
                    );
                  }}
                />
              )}
            </>
          );
        })()}

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Submit Invoice — {selectedContract?.project_address || selectedContract?.project_name}</DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            {/* Invoice Type Toggle */}
            {selectedContract && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 2, bgcolor: '#f8f9ff', borderRadius: 2, border: '1px solid #e8eaf6' }}>
                <Typography variant="subtitle2" fontWeight={700} color="#1a237e">Contract Reference</Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  <Box><Typography variant="caption" color="text.secondary">Contract #</Typography><Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>{selectedContract.id.slice(-8).toUpperCase()}</Typography></Box>
                  {selectedContract.po_number && <Box><Typography variant="caption" color="text.secondary">PO #</Typography><Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>{selectedContract.po_number}</Typography></Box>}
                  <Box><Typography variant="caption" color="text.secondary">Address</Typography><Typography variant="body2" fontWeight={600}>{selectedContract.project_address || selectedContract.project_name}</Typography></Box>
                  {selectedContract.project_address && selectedContract.project_name && <Box><Typography variant="caption" color="text.secondary">Project</Typography><Typography variant="body2" fontWeight={600}>{selectedContract.project_name}</Typography></Box>}
                  {selectedContract.scope_title && <Box><Typography variant="caption" color="text.secondary">Scope</Typography><Typography variant="body2" fontWeight={600}>{selectedContract.scope_title}</Typography></Box>}
                </Box>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 0.5, pt: 1, borderTop: '1px dashed #c5cae9' }}>
                  <Box><Typography variant="caption" color="text.secondary">Contract Amt</Typography><Typography variant="body2" fontWeight={600}>${parseFloat(selectedContract.contract_amount||0).toLocaleString(undefined,{minimumFractionDigits:2})}</Typography></Box>
                  <Box><Typography variant="caption" color="text.secondary">Invoiced</Typography><Typography variant="body2" fontWeight={600}>${parseFloat(selectedContract.invoiced_to_date||0).toLocaleString(undefined,{minimumFractionDigits:2})}</Typography></Box>
                  <Box><Typography variant="caption" color="text.secondary">Remaining</Typography><Typography variant="body2" fontWeight={700} color="success.main">${(parseFloat(selectedContract.contract_amount||0)-parseFloat(selectedContract.invoiced_to_date||0)).toLocaleString(undefined,{minimumFractionDigits:2})}</Typography></Box>
                </Box>
              </Box>
            )}
            {selectedContract && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 2, bgcolor: '#fff8e1', borderRadius: 2, border: '1px solid #ffe0a6' }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#7a5900' }}>Contractor (From)</Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Name</Typography>
                    <Typography variant="body2" fontWeight={600}>{selectedContract.vendor_name || [selectedContract.first_name, selectedContract.last_name].filter(Boolean).join(' ') || '—'}</Typography>
                  </Box>
                  {selectedContract.contractor_email && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Email</Typography>
                      <Typography variant="body2" fontWeight={600}>{selectedContract.contractor_email}</Typography>
                    </Box>
                  )}
                  {selectedContract.trade_category && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Trade</Typography>
                      <Typography variant="body2" fontWeight={600}>{selectedContract.trade_category}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
<TextField
              label="Invoice Number (auto-generated if blank)"
              value={form.invoice_number}
              onChange={e => setForm({ ...form, invoice_number: e.target.value })}
              fullWidth
            />
            <TextField
              label="Invoice Date" type="date" value={form.invoice_date}
              onChange={e => setForm({ ...form, invoice_date: e.target.value })}
              fullWidth InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Amount ($)" type="number" value={form.amount}
              onChange={e => setForm({ ...form, amount: e.target.value })}
              fullWidth required inputProps={{ min: 0, step: '0.01' }}
            />
            <TextField
              label="Description / Work Completed" multiline rows={3}
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              fullWidth
            />
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Attach Invoice Document (optional)</Typography>
              <FileUpload onFilesChange={(files) => setForm({ ...form, attachments: files })} accept=".pdf,.jpg,.png,.doc,.docx" />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSubmit} disabled={loading || !form.amount}>
              {loading ? 'Submitting...' : 'Submit Invoice'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
}
